import React from 'react';

/**
 * This file is a part of the Promvc framework.
 * 
 * (c) ThemePlexi
 * 
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

/**
 * String methods
 * 
 * @version 1.0.0
 */
const StringMethods = ( function () {

	/**
	 * Declare private properties
	 * @private
	 */
	
	/**
	 * Public string methods api.
	 * 
	 * @since 1.0.0
	 */
	const Util = {

		/**
		 * Add class attribute to html element.
		 * 
		 * @param  {Object} htmlDOMObj The html element to add class attribute to. 
		 * @param  {String} str        The class name to add. 
		 * @return {void}
		 */
		addHTMLClass: function ( htmlDOMObj, str )
		{
			str = str.replace( /\\/, '' );

			htmlDOMObj.className = htmlDOMObj.className + ' ' + str;
		},

		/**
		 * Create a unique id.
		 * 
		 * @param {String} pattern The pattern to use.
		 * @return {String}
		 */
		createUUID: function ( pattern )
		{
			pattern = pattern || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
		
			return pattern.replace( /[xy]/g, ( c ) => {
				const r = ( Math.random() * 16 ) | 0;
				const v = c === 'x' ? r : ( ( r & 0x3 ) | 0x8 );
				return v.toString( 16 );
			});
		},

		/**
		 * Get html content inside tags.
		 * 
		 * @param  {String} htmlStr The html to strip down.
		 * @return {String}
		 */
		decodeHTMLEntities: function ( htmlStr )
		{
			const element = document.createElement( 'span' );

			if ( typeof htmlStr === 'string' ) {
				// strip script/html tags
				htmlStr = htmlStr.replace( /<script[^>]*>([\S\s]*?)<\/script>/gmi, '' );
				htmlStr = htmlStr.replace( /<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '' );

				element.innerHTML = htmlStr;
				htmlStr = element.textContent;
				element.textContent = '';
			}

			return htmlStr;
		},

		extractHtmlBody: function ( htmlStr )
		{
			if ( typeof htmlStr !== 'string' ) {
				return new Error( 'Invalid parameter passed to function. Expected a string value instead.' );
			}

			const match = htmlStr.match( /<body>(.*)<\/body>/ );

			if ( match && match[ 1 ] !== '' ) {
				return match[ 1 ];
			}

			return false;
		},

		getCurrentPage: function ( url )
		{
			const arr = this.rtrim( url, '/' ).split( '/' );
			return arr[ arr.length - 1 ];
		},

		hasHTMLClass: function ( htmlDOMObj, str )
		{
			str = str.replace( /\\/, '' );
			const exp = new RegExp( `${str}(?=(?:\\s|$))+` );

			const htmlClassList = htmlDOMObj.className;
			const match = htmlClassList.match( exp );

			return match != null ? true : false;
		},

		leadingNum: function ( num )
		{
			return ( num < 10 ) ? '0' + num : num;
		},

		minifyHtml: function ( htmlStr )
		{
			if ( typeof htmlStr !== 'string' ) {
				return new Error( 'Invalid parameter passed to function. Expected a string value instead.' );
			}

			const reqexp = {
				'0': {
					pattern: />[^\S ]+/g,
					replace: '>'
				},
				'1': {
					pattern: /[^\S ]+</g,
					replace: '<'
				},
				'2': {
					pattern: /(\s)+/g,
					replace: ' '
				},
				'3': {
					pattern: />(\s)+/g,
					replace: '>'
				}
			};

			for ( let expr in reqexp ) {
				// Iterate only for non inherited properties
				if ( Object.prototype.hasOwnProperty.call( reqexp, expr ) ) {
					const { pattern, replace } = reqexp[ expr ];
					htmlStr = htmlStr.replace( pattern, replace );
				}
			}

			return htmlStr;
		},

		removeHTMLClass: function ( htmlDOMObj, str )
		{
			str = str.replace( /\\/, '' );
			const exp = new RegExp( `${str}(?=(?:\\s|$))+` );

			// remove matching class and extra space
			let htmlClassList = htmlDOMObj.className.replace( exp, '' );
			htmlClassList = htmlClassList.replace( /\s{2,}/, ' ' );

			htmlDOMObj.className = htmlClassList;
		},

		/**
		 * Strip whitespace (or other characters) from the end of a string.
		 * 
		 * @param  {String} str The input string.
		 * @param  {String} chr Simply list all characters that you want to be stripped
		 * @return {String}
		 */
		rtrim: function ( str, chr )
		{
			chr = chr || ' \n\r\t\v\0';
			const regex = new RegExp( '[' + chr + ']+$' );

			return str.replace( regex, '' );
		},

		/**
		 * Change the case of the first character of a string to uppercase.
		 * 
		 * @param  {String} str The string to change.
		 * @return {String}
		 */
		ucfirst: function ( str )
		{
			return str.charAt( 0 ).toUpperCase() + str.slice( 1 );
		},

		/**
		 * Change url slugs into readable text.
		 * 
		 * @param  {String} slug 
		 * @return {String}
		 */
		unslugText: function( slug )
		{
			// Abort for invalid input
			if ( typeof slug !== 'string' || slug.trim() === '' ) {
				return slug;
			}

			return slug
				// Replace underscores/dashes with spaces
				.replace( /[-_]/g, ' ' )
				// Insert a space before any capital letter (except at the start)
				.replace( /([a-z])([A-Z])/g, '$1 $2' )
				// Split into words
				.split( /\s+/ )
				// Capitalize first letter of each word
				.map( word => this.ucfirst( word ) )
				// Join back into a single string
				.join( ' ' );
		}		
		
	};

	return Util;

}());

/**
 * Module exports
 */
export { StringMethods };